import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import doctorMasterDataService from "../services/doctorMasterDataService";

const doctorsMasterDataCollectionName = "doctors_master_data";
export const useDoctorMasterData = () => {
  return useQuery({
    queryKey: [doctorsMasterDataCollectionName],
    queryFn: () => doctorMasterDataService.getAll(),
    staleTime: 0, // Always consider data stale to ensure fresh updates
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useDoctorMasterDataWithoutPagination = () => {
  return useQuery({
    queryKey: [doctorsMasterDataCollectionName, "withoutPagination"],
    queryFn: () => doctorMasterDataService.getAllWithoutPagination(),
    staleTime: 0, // Always consider data stale to ensure fresh updates
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  });
};

export const useDoctorMasterDataById = (id) => {
  return useQuery({
    queryKey: [doctorsMasterDataCollectionName, id],
    queryFn: () => doctorMasterDataService.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const useAddDoctorsMasterData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorMasterDataService.add,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName],
      });
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName, "withoutPagination"],
      });
    },
  });
};

export const useEditDoctorsMasterData = (hcpId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorMasterDataService.edit,
    onSuccess: () => {
      // Invalidate all relevant queries to ensure real-time updates
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName],
      });
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName, "withoutPagination"],
      });
      queryClient.invalidateQueries({
        queryKey: ["hcp", hcpId],
      });
    },
  });
};

export const useDeleteDoctorsMasterData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorMasterDataService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName],
      });
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName, "withoutPagination"],
      });
    },
  });
};

export const useCreateDoctorFromUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorMasterDataService.createFromUserData,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName],
      });
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName, "withoutPagination"],
      });
    },
  });
};

export const useDoctorMasterDataByEmail = (email) => {
  return useQuery({
    queryKey: [doctorsMasterDataCollectionName, "email", email],
    queryFn: () => doctorMasterDataService.getByEmail(email),
    enabled: !!email,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const useUpdateAPONumberForExistingRecords = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorMasterDataService.updateAPONumberForExistingRecords,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName],
      });
      queryClient.invalidateQueries({
        queryKey: [doctorsMasterDataCollectionName, "withoutPagination"],
      });
    },
  });
};

export const useDoctorMasterDataByHCP = (searchTerm) => {
  return useQuery({
    queryKey: [doctorsMasterDataCollectionName, "hcp", searchTerm],
    queryFn: () => doctorMasterDataService.getHCP(searchTerm),
    enabled: !!searchTerm,
    refetchOnWindowFocus: false,
  });
};

export const useGetHCPByNationalId = (nationalId) => {
  return useQuery({
    queryKey: [doctorsMasterDataCollectionName, "national_id", nationalId],
    queryFn: () => doctorMasterDataService.getHCPByNationalId(nationalId),
    enabled: !!nationalId,
  });
};
